import { IAppState } from 'src/store/types';
import {
  IFilterAllData,
  ListOfKeysFilterAllData,
  PlacementShowModule,
} from 'src/typings/filter';
import {
  AIChatComplexContext,
  AIChatCurrency,
  AIChatProgressTrace,
} from 'src/typings/ai-chat';
import { extractAIChatCurrency } from './currency';

export const getAIChatState = (state: IAppState) => state.aiChat;
export const getAIChatIsOpen = (state: IAppState) => getAIChatState(state).isOpen;
export const getAIChatDraft = (state: IAppState) => getAIChatState(state).draft;
export const getAIChatStatus = (state: IAppState) => getAIChatState(state).status;
export const getAIChatMessages = (state: IAppState) => getAIChatState(state).messages;
export const getAIChatError = (state: IAppState) => getAIChatState(state).error;
export const getAIChatConversationId = (state: IAppState) => getAIChatState(state).conversationId;
export const getAIChatConversationContext = (state: IAppState) => (
  getAIChatState(state).conversationContext
);
export const getAIChatActiveTrace = (state: IAppState): AIChatProgressTrace | null => (
  getAIChatState(state).activeTrace
);

export const AI_CHAT_ALL_PLACEMENT_TYPE = 'all';

export const getAIChatCandidates = (state: IAppState): IFilterAllData[] => {
  const seenIds = new Set<string>();
  const catalogs = Object.values(state.requests.filter.data || {});

  return catalogs.flatMap((catalog) => Object.values(catalog.filter_all_data || {}))
    .filter((candidate) => (
      candidate[ListOfKeysFilterAllData.placementStatusShowInModule] !== PlacementShowModule.hide
    ))
    .filter((candidate) => {
      const id = candidate[ListOfKeysFilterAllData.id];
      if (id === null || id === undefined) return false;
      const canonicalId = String(id);
      if (seenIds.has(canonicalId)) return false;
      seenIds.add(canonicalId);
      return true;
    });
};

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const getEntryCurrency = (entry: unknown, field: string): AIChatCurrency | null => {
  if (!isRecord(entry)) return null;
  const fieldEntry = entry[field];
  if (isRecord(fieldEntry)) {
    return extractAIChatCurrency(fieldEntry['value-prefix'])
      || extractAIChatCurrency(fieldEntry['value-sufix']);
  }
  const prefix = entry['value-prefix'];
  const sufix = entry['value-sufix'];
  return extractAIChatCurrency(isRecord(prefix) ? prefix[field] : prefix)
    || extractAIChatCurrency(isRecord(sufix) ? sufix[field] : sufix);
};

const getNestedConfigCurrency = (config: unknown, field: string): AIChatCurrency | null => {
  if (!isRecord(config)) return null;
  return Object.values(config).reduce<AIChatCurrency | null>((currency, section) => (
    currency || getEntryCurrency(section, field)
  ), null);
};

const getDisplayConfigCurrency = (config: unknown, field: string): AIChatCurrency | null => {
  if (!isRecord(config)) return null;
  return getEntryCurrency(config.placement_info_config, field)
    || getEntryCurrency(config.placement_grouped_info_config, field)
    || getNestedConfigCurrency(config.filter_info_config, field)
    || getNestedConfigCurrency(config.placement_grouped_tile_config, field)
    || getNestedConfigCurrency(config.pop_up_info_config, field)
    || getEntryCurrency(config.hover_pop_up_short_info, field);
};

export const getAIChatPrimaryPriceCurrency = (state: IAppState): AIChatCurrency | null => {
  const translations = state.requests.translations?.data;
  const totalPriceKey = ListOfKeysFilterAllData.placementTotalPrice;
  const pricePerSqMKey = ListOfKeysFilterAllData.placementPricePerSqM;
  const catalogs = Object.values(state.requests.filter.data || {});

  const displayCurrency = (field: string) => catalogs.reduce<AIChatCurrency | null>((currency, catalog) => (
    currency || getDisplayConfigCurrency(catalog['placement-info-config'], field)
  ), null);

  return displayCurrency(totalPriceKey)
    || extractAIChatCurrency(translations?.[totalPriceKey])
    || displayCurrency(pricePerSqMKey)
    || extractAIChatCurrency(translations?.[pricePerSqMKey])
    || getAIChatCandidates(state).reduce<AIChatCurrency | null>((currency, candidate) => (
      currency || extractAIChatCurrency(candidate[totalPriceKey])
    ), null)
    || null;
};

const localizedValue = (
  value: { [key: string]: string } | null | undefined,
  language: string,
  fallbackLanguage: string,
): string | null => {
  if (!value) return null;
  return value[language]
    || value[fallbackLanguage]
    || Object.values(value).find((candidate) => typeof candidate === 'string' && candidate.length > 0)
    || null;
};

export const getAIChatComplexName = (state: IAppState): string | null => {
  const complex = state.crosstable?.complex;
  if (!complex) return null;

  const language = state.app?.currentLang || complex.language || 'en';
  const fallbackLanguage = complex.language || language;
  return localizedValue(complex.title, language, fallbackLanguage);
};

export const getAIChatComplexContext = (state: IAppState): AIChatComplexContext | null => {
  const complex = state.crosstable?.complex;
  const complexId = state.app?.complexId;
  if (!complex || complexId === null || complexId === undefined) return null;
  if (String(complex.id) !== String(complexId)) return null;

  const language = state.app.currentLang || complex.language || 'en';
  const fallbackLanguage = complex.language || language;
  const { location } = complex;
  const locationValue = (
    manual: { [key: string]: string } | null | undefined,
    automatic: { [key: string]: string } | null | undefined,
  ) => localizedValue(manual, language, fallbackLanguage)
    || localizedValue(automatic, language, fallbackLanguage);

  return {
    complex: {
      id: String(complexId),
      title: localizedValue(complex.title, language, fallbackLanguage),
      address: localizedValue(complex.address, language, fallbackLanguage),
      country: locationValue(location?.countryManual, location?.countryAutoCalculated),
      city: locationValue(location?.cityManual, location?.cityAutoCalculated),
      district: locationValue(location?.districtManual, location?.districtAutoCalculated),
    },
    language,
    placementType: AI_CHAT_ALL_PLACEMENT_TYPE,
    primaryPriceCurrency: getAIChatPrimaryPriceCurrency(state),
  };
};
